import { useMemo, useState } from 'react';
import { Source, Layer, Marker, Popup } from 'react-map-gl/mapbox';
import CoordinatesLink from '../Base/CoordinatesLink';
import { linkQuality, qualityColor } from '../../../helpers/linkQuality';

const GATEWAY_COLOR = 'rgb(83, 208, 122)';
const GATEWAY_INACTIVE_BORDER = '#000000';
const LINE_LAYER_ID = 'gateway-lines-layer';
const LINE_HOVER_LAYER_ID = 'gateway-lines-hover-layer';

// Expresión Mapbox GL que mapea la propiedad `quality` a un color
const QUALITY_COLOR_EXPR = [
  'step', ['get', 'quality'],
  '#9ca3af',
  0,  '#dc2626',
  40, '#d97706',
  70, '#16a34a',
];

function GatewayLayer({ gateways, links, selectedGatewayIds, hoveredDetectorId }) {
  const [activeGateway, setActiveGateway] = useState(null);

  const visibleGateways = useMemo(
    () => selectedGatewayIds === null ? gateways : gateways.filter(g => selectedGatewayIds.has(g.gatewayId)),
    [gateways, selectedGatewayIds]
  );

  const linesGeojson = useMemo(() => {
    const gatewayById = Object.fromEntries(visibleGateways.map(g => [g.gatewayId, g]));
    const features = [];

    for (const link of links) {
      for (const gw of link.gateways) {
        const gateway = gatewayById[gw.gatewayId];
        if (!gateway?.location) continue;
        features.push({
          type: 'Feature',
          properties: {
            featureKey: `${link.detectorName}||${gw.gatewayId}`,
            detectorId: link.detectorId,
            snr: gw.avgSnr,
            rssi: gw.avgRssi,
            dr: gw.avgDr,
            quality: linkQuality(gw.avgSnr, gw.avgDr) ?? -1,
            count: gw.count,
            detectorName: link.detectorName,
            gatewayName: gateway.name,
          },
          geometry: {
            type: 'LineString',
            coordinates: [
              [link.longitude, link.latitude],
              [gateway.location.longitude, gateway.location.latitude],
            ],
          },
        });
      }
    }

    return { type: 'FeatureCollection', features };
  }, [visibleGateways, links]);

  // Gateways con al menos una conexión en el período analizado
  const activeGatewayIds = useMemo(
    () => new Set(links.flatMap(l => l.gateways.map(g => g.gatewayId))),
    [links]
  );

  // Link del detector con hover activo (para el tooltip y el filtro de resaltado)
  const hoveredLink = useMemo(
    () => hoveredDetectorId ? (links.find(l => l.detectorId === hoveredDetectorId) ?? null) : null,
    [links, hoveredDetectorId]
  );

  return (
    <>
      <Source id="gateway-lines" type="geojson" data={linesGeojson}>
        <Layer
          id={LINE_LAYER_ID}
          type="line"
          paint={{
            'line-color': QUALITY_COLOR_EXPR,
            'line-width': 3,
            'line-opacity': 0.9,
            'line-dasharray': [4, 3],
          }}
        />
        <Layer
          id={LINE_HOVER_LAYER_ID}
          type="line"
          filter={hoveredDetectorId ? ['==', ['get', 'detectorId'], hoveredDetectorId] : ['boolean', false]}
          paint={{
            'line-color': QUALITY_COLOR_EXPR,
            'line-width': 6,
            'line-opacity': 1,
          }}
        />
      </Source>

      {/* Tooltip al hacer hover sobre un detector: muestra todos sus gateways */}
      {hoveredLink && !activeGateway && (
        <Popup
          longitude={hoveredLink.longitude}
          latitude={hoveredLink.latitude}
          closeButton={false}
          closeOnClick={false}
          anchor="bottom"
          style={{ pointerEvents: 'none' }}
          offset={[0, -20]}
        >
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, minWidth: 180 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{hoveredLink.detectorName}</div>
            {hoveredLink.gateways.map((gw, i) => {
              const gateway = gateways.find(g => g.gatewayId === gw.gatewayId);
              const q = linkQuality(gw.avgSnr, gw.avgDr);
              return (
                <div key={i} style={{ marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: 500, flex: 1 }}>{gateway?.name ?? gw.gatewayId}</span>
                    {q != null && (
                      <>
                        <div style={{ width: 48, height: 5, borderRadius: 3, background: '#e5e7eb', overflow: 'hidden' }}>
                          <div style={{ width: `${q}%`, height: '100%', background: qualityColor(q), borderRadius: 3 }} />
                        </div>
                        <span style={{ fontWeight: 700, color: qualityColor(q), minWidth: 28, textAlign: 'right', fontSize: 11 }}>{q}%</span>
                      </>
                    )}
                  </div>
                  <span style={{ color: '#666', fontSize: 11 }}>
                    SNR {gw.avgSnr != null ? gw.avgSnr.toFixed(1) : '—'} dB · RSSI {gw.avgRssi != null ? Math.round(gw.avgRssi) : '—'} dBm · {gw.count} envíos
                  </span>
                </div>
              );
            })}
          </div>
        </Popup>
      )}

      {visibleGateways.filter(gw => gw.location).map(gw => (
        <Marker
          key={gw._id}
          longitude={gw.location.longitude}
          latitude={gw.location.latitude}
          anchor="center"
        >
          <div
            onClick={e => { e.stopPropagation(); setActiveGateway(gw._id === activeGateway ? null : gw._id); }}
            title={gw.name}
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: activeGatewayIds.has(gw.gatewayId) ? GATEWAY_COLOR : '#9ca3af',
              border: activeGatewayIds.has(gw.gatewayId) ? '2px solid white' : `3px solid ${GATEWAY_INACTIVE_BORDER}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              cursor: 'pointer',
            }}
          />
        </Marker>
      ))}

      {visibleGateways.map(gw => {
        if (activeGateway !== gw._id) return null;
        const connectedDetectors = links
          .filter(l => l.gateways.some(g => g.gatewayId === gw.gatewayId))
          .map(l => {
            const g = l.gateways.find(g => g.gatewayId === gw.gatewayId);
            return { detectorId: l.detectorId, locationName: l.locationName, snr: g?.avgSnr, rssi: g?.avgRssi, dr: g?.avgDr, count: g?.count };
          });
        return (
          <Popup
            key={gw._id}
            longitude={gw.location.longitude}
            latitude={gw.location.latitude}
            onClose={() => setActiveGateway(null)}
            closeOnClick={false}
            anchor="top"
          >
            <div style={{ fontFamily: "'Inter', sans-serif", minWidth: 160, fontSize: 12 }}>
              <strong style={{ fontSize: 13 }}>{gw.name}</strong>
              <div style={{ color: '#555', marginBottom: 6, fontFamily: 'monospace', fontSize: 11 }}>{gw.gatewayId}</div>
              {connectedDetectors.length === 0 ? (
                <div style={{ opacity: 0.6 }}>Sin conexiones en 24h</div>
              ) : (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Detectores (24h)</div>
                  <div style={connectedDetectors.length > 6 ? { maxHeight: 300, overflowY: 'auto', paddingRight: 4 } : undefined}>
                    {connectedDetectors.map((d, i) => (
                      <div key={i} style={{ marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
                          <span style={{ fontWeight: 500, flex: 1 }}>{d.detectorId}{d.locationName ? ` (${d.locationName})` : ''}</span>
                          {(() => { const q = linkQuality(d.snr, d.dr); return q != null ? <span style={{ fontWeight: 700, color: qualityColor(q), fontSize: 11 }}>{q}%</span> : null; })()}
                        </div>
                        <span style={{ color: '#666', fontSize: 11 }}>
                          SNR {d.snr != null ? d.snr.toFixed(1) : '—'} dB · RSSI {d.rssi != null ? Math.round(d.rssi) : '—'} dBm · {d.count} envíos
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div style={{ marginTop: 8 }}>
                <CoordinatesLink latitude={gw.location.latitude} longitude={gw.location.longitude} />
              </div>
            </div>
          </Popup>
        );
      })}
    </>
  );
}

export default GatewayLayer;
